import type { ReactNode } from 'react';

import { ACCOUNT_LIST_ROW_CLASS, AccountListRow } from '@/components/account/AccountListRow';
import { Divider } from '@/components/ui/divider';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Switch } from '@/components/ui/switch';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { useAnnouncementBannerSetting } from '@/hooks/useAnnouncementBannerSetting';
import { useCognitoGroups } from '@/hooks/useCognitoGroups';
import { useColors } from '@/hooks/useColors';
import {
  useInboxQuestionGroupCardsOptIn,
  useInboxQuestionGroupCardsOptInStore,
} from '@/services/stores/inboxQuestionGroupCardsOptInStore';

/**
 * The account page's staff section, "Primum": the tools that act on the platform rather than on
 * the account, kept apart from Settings so a staff member finds them in one place. Developer
 * settings used to open only from a triple tap on the version number, which nobody found.
 *
 * The inquiry groups and announcement banner switches show only for the Primum group, and the inbox checks the group again
 * before it shows a nested card. Act as comes in through `actAs` and shows whenever the account
 * switcher allows it, which is decided by its own hook rather than here. The section renders
 * nothing when it would hold neither.
 */
export function PrimumSection({
  actAs,
  onOpenDeveloperSettings,
}: {
  actAs?: ReactNode;
  /** Opens Developer settings. Passed only for the accounts allowed to see them. */
  onOpenDeveloperSettings?: () => void;
}) {
  const { isPrimumUser } = useCognitoGroups();

  const optedIn = useInboxQuestionGroupCardsOptIn();

  const setOptedIn = useInboxQuestionGroupCardsOptInStore((state) => state.setOptedIn);

  const announcementBanner = useAnnouncementBannerSetting();

  if (!isPrimumUser && !actAs && !onOpenDeveloperSettings) {
    return null;
  }

  return (
    <VStack className="mb-8 w-full">
      <Heading size="h3-lg" className="mb-4 text-primary-500">
        Primum
      </Heading>

      {/* The same gap the Settings list puts between its rows, so the two sections match. */}
      <VStack className="w-full gap-4">
        {actAs}

        {isPrimumUser ? (
          <SwitchRow
            title="Inquiry groups (beta)"
            subtitle="Group inquiries by question on this device"
            value={optedIn}
            onValueChange={setOptedIn}
          />
        ) : null}

        {isPrimumUser ? (
          <SwitchRow
            title="Announcement banner"
            subtitle="Show announcements, applied on reload"
            value={announcementBanner.enabled === true}
            disabled={announcementBanner.enabled === null}
            onValueChange={announcementBanner.setEnabled}
          />
        ) : null}

        {onOpenDeveloperSettings ? (
          <AccountListRow title="Developer settings" onPress={onOpenDeveloperSettings} />
        ) : null}
      </VStack>
    </VStack>
  );
}

/**
 * A switch laid out as an account list row: the same height, type and closing divider as the
 * rows around it, so the section reads as one list.
 */
function SwitchRow({
  title,
  subtitle,
  value,
  disabled,
  onValueChange,
}: {
  title: string;
  subtitle: string;
  value: boolean;
  disabled?: boolean;
  onValueChange: (value: boolean) => void;
}) {
  const { colors } = useColors();

  return (
    <VStack className={ACCOUNT_LIST_ROW_CLASS}>
      <HStack className="w-full items-center justify-between gap-4">
        <VStack className="min-w-0 flex-1">
          <Text>{title}</Text>

          <Text size="sm" className="text-typography-600">
            {subtitle}
          </Text>
        </VStack>

        <Switch
          accessibilityLabel={title}
          trackColor={{ true: colors.secondary[500], false: colors.background[200] }}
          value={value}
          disabled={disabled}
          onValueChange={onValueChange}
        />
      </HStack>

      <Divider />
    </VStack>
  );
}
